import { CreditCard, ExternalLink } from 'lucide-react'
import * as React from 'react'
import { Button } from '../components/button'
import { Chip } from '../components/chip'
import { Loader } from '../components/loader'
import { ParsleyMark } from '../components/parsley-mark'
import { PillButton } from '../components/pill-button'
import { cn } from '../lib/utils'

/**
 * The content of `/settings/subscription` — the **destination** a subscriber opens deliberately to
 * understand their account, where `BillingBanner` is the interruption they see in passing.
 *
 * ## A stack of solid slabs, not a bordered card
 *
 * The design rule for this surface is that **solid colour defines areas**: no borders, no hairlines,
 * no divider rules anywhere. Three slabs — plan, status, footer — separated by a 6px gap that the
 * page background shows through. Outer corners 22px, inner 10px, which is what gives the "sliced"
 * look. Which slabs render is derived from `state`, never passed.
 *
 * ## Two ramps
 *
 * The **status slab** reuses `BillingBanner`'s severity ramp 1:1 (amber → deep green) so this page
 * looks like the banner the user clicked through from. Healthy states get a calm green the banner
 * never renders.
 *
 * The **plan slab** carries its own hierarchy, and it is the one place this component is allowed a
 * flourish: cream for everything → green tint + leaf watermark for `trialing` ("on the way") → deep
 * green gradient + white ink for `active` (the reward). Trial must **not** read as active — no
 * gradient, no white ink — or the destination loses its pull.
 *
 * ## Everything is a string
 *
 * `@parsleyai/ui` is i18n-free, so all copy arrives resolved. Day counts and dates arrive
 * **pre-formatted** (`"21"`, `"of 14 grace days left"`, `"April 23, 2027"`): no date math, no date
 * library. `meter` is a plain 0–1 fraction. Cohort is deliberately **not** a prop — the beta/trial
 * difference is entirely in the strings, the same resolution `BillingBanner` reached.
 */
export type SubscriptionCardState =
  | 'loading'
  | 'provisioning'
  | 'none'
  | 'trialing'
  | 'active'
  | 'cancel_pending'
  | 'past_due'
  | 'grace_expired'
  | 'paused'
  | 'canceled'
  /** `unpaid` | `incomplete` | `incomplete_expired` — one screen; the user's next step is identical. */
  | 'terminal'

type SlabTone = 'quiet' | 'good' | 'amber' | 'deep' | 'gray'
type PlanHero = 'deep' | 'lite' | undefined
/** Which button the primary action renders as. Escalates like `BillingBanner`'s, plus the hero. */
type ActionKind = 'hero' | 'solid' | 'on-deep' | 'pill-primary' | 'pill-secondary'

/**
 * State → slab tone, which slabs render, and the plan-slab treatment. Exported so the app can ask
 * what a state looks like without duplicating the table — the same enforcement pattern as
 * `BILLING_BANNER_STATES`: a caller cannot give `past_due` the hero treatment by mistake.
 */
export const SUBSCRIPTION_CARD_STATES: Record<
  Exclude<SubscriptionCardState, 'loading'>,
  { tone: SlabTone; plan: boolean; foot: boolean; hero?: PlanHero }
> = {
  provisioning: { tone: 'quiet', plan: false, foot: false },
  // Gray, not the handoff's soft green: subscriptions are provisioned automatically when the
  // profile doc lands, so `none` means provisioning failed. That's an error, and there is no
  // self-serve signup path to offer instead — `/subscribe` resumes a paused subscription, it
  // cannot create one. Sits with `canceled` and `terminal` rather than the healthy states.
  none: { tone: 'gray', plan: false, foot: false },
  trialing: { tone: 'good', plan: true, foot: true, hero: 'lite' },
  active: { tone: 'good', plan: true, foot: true, hero: 'deep' },
  cancel_pending: { tone: 'amber', plan: true, foot: true },
  past_due: { tone: 'amber', plan: true, foot: true },
  grace_expired: { tone: 'deep', plan: true, foot: true },
  paused: { tone: 'amber', plan: true, foot: true },
  canceled: { tone: 'gray', plan: true, foot: true },
  terminal: { tone: 'gray', plan: true, foot: true }
}

/**
 * Slab fills. The `color-mix` values are arbitrary utilities rather than named tokens: they're
 * specific to this component, and the DS already uses inline `color-mix` where a mix has one
 * consumer (see `Tag tone="amber"` and `PillButton variant="danger"`). Adding five mixes to the
 * shared preset for one block would be the more invasive choice.
 *
 * `deep` lightens one step in dark mode — `--brand-green-deep` resolves near 950 there and sits too
 * close to the page background to read as a distinct surface.
 */
const SLAB: Record<SlabTone, string> = {
  quiet: 'bg-card-sunken',
  good: cn(
    'bg-[color-mix(in_oklab,oklch(var(--brand-green-500))_11%,oklch(var(--card)))] text-brand-green-800',
    'dark:bg-[color-mix(in_oklab,oklch(var(--brand-green-700))_34%,oklch(var(--card)))] dark:text-brand-green-200'
  ),
  amber: cn(
    'bg-[color-mix(in_oklab,oklch(var(--amber-500))_14%,oklch(var(--card)))] text-amber-800',
    'dark:bg-[color-mix(in_oklab,oklch(var(--amber-500))_22%,oklch(var(--card)))] dark:text-amber-200'
  ),
  deep: 'bg-brand-green-deep text-brand-green-on-deep dark:bg-brand-green-900 dark:text-brand-green-50',
  gray: cn(
    'bg-[color-mix(in_oklab,oklch(var(--gray-400))_16%,oklch(var(--card)))] text-foreground',
    'dark:bg-[color-mix(in_oklab,oklch(var(--gray-600))_26%,oklch(var(--card)))]'
  )
}

/** The `good` slab's headline steps one shade past its body ink; every other tone inherits. */
const HEAD_INK: Partial<Record<SlabTone, string>> = {
  good: 'text-brand-green-900 dark:text-brand-green-100'
}

/**
 * `Chip variant="meta"` tone inks are **fill** tones and fail AA at 12px on the composited stack —
 * `critical` measures 3.76:1 in light. Same class of bug as `BillingBanner`'s `Tag tone="amber"`
 * finding; both fold into #1214, and both overrides disappear when it lands a real ink role.
 * `onImage` is fixed white-on-photo and passes untouched.
 */
const CHIP_INK: Record<string, string> = {
  critical: 'text-red-800 dark:text-red-300',
  accent: 'text-amber-800 dark:text-amber-200',
  neutral: 'text-gray-700 dark:text-gray-200'
}

export interface SubscriptionPlan {
  /** The public plan name, e.g. "Handful". Not the Stripe tier key. */
  name: string
  /** Pre-formatted, e.g. "$9.99" */
  price: string
  /** e.g. "per month" */
  per: string
  /** The marketed-as line, e.g. "Up to 7 dinners a week · quick to involved". Facts, not a self-assessment. */
  desc: string
}

/** Resolved copy. Every string is app-translated; nothing here is formatted by the component. */
export interface SubscriptionCardLabels {
  /** Plan-slab eyebrow, e.g. "Your plan" */
  eyebrow?: string
  /** Status chip beside the plan name, e.g. "Beta access" */
  chip?: string
  /** Chip colour. `accent` and `critical` carry an AA ink override; `onImage` is for the hero. */
  chipTone?: 'neutral' | 'accent' | 'critical' | 'onImage'
  /** Replaces "per month" and strikes the price, e.g. "Free during beta". */
  priceNote?: string
  /** Status-slab kicker, e.g. "Beta access · free for six months" */
  kicker?: string
  /** Status headline. Rendered when there is no `figure`. */
  head?: string
  /** Pre-formatted count, e.g. "132". Takes the headline's place when present. */
  figure?: string
  /** Unit beside the figure, e.g. "days left" */
  figureUnit?: string
  /** Body copy. `ReactNode` so the app can bold a date without this parsing markup. */
  body?: React.ReactNode
  /** Reassurance beside the hero CTA, e.g. "A minute to set up · nothing charged until…" */
  hint?: string
  /**
   * The card on file, pre-formatted — e.g. "Visa ending 4242". Its own field rather than prose
   * inside {@link body}, so a state can't quietly stop answering "which card?".
   */
  card?: string
  /** Footer action, e.g. "Manage billing" */
  manage?: string
  /** Footer fine print. */
  fine?: string
  /** `aria-label` for the loading skeleton. Defaults to "Loading subscription". */
  loadingLabel?: string
}

export interface SubscriptionStatusCardProps {
  state: SubscriptionCardState
  /** Omitted on states with no plan slab (`provisioning`, `none`). */
  plan?: SubscriptionPlan
  labels?: SubscriptionCardLabels
  /** 0–1, the remaining fraction. Renders a meter under the figure. */
  meter?: number
  /** The single primary action. Its destination is an app concern. */
  primaryAction?: {
    label: string
    onClick: () => void
    kind?: ActionKind
    /** The action is in flight — spinner, disabled, `aria-busy`. Applies to every `kind`. */
    pending?: boolean
  }
  /** "Manage billing" — the Stripe portal hand-off. */
  secondaryAction?: { onClick: () => void }
  className?: string
}

const OUTER = 'first:rounded-t-[22px] last:rounded-b-[22px]'
const SLAB_BASE = cn('rounded-[10px] px-7 py-6 max-md:px-5 max-md:py-5', OUTER)

/** ~190px leaf bleeding off the top-right. Never redrawn — always the real mark. */
function HeroLeaf({ className }: { className: string }) {
  return (
    <span aria-hidden className={cn('pointer-events-none absolute -right-[38px] -top-[30px] w-[190px] leading-none', className)}>
      <ParsleyMark filled className="h-[170px] w-full" />
    </span>
  )
}

function Skeleton({ label }: { label: string }) {
  const bar = 'animate-pulse rounded-[7px] bg-[color-mix(in_oklab,oklch(var(--foreground))_9%,transparent)]'
  return (
    // biome-ignore lint/a11y/useSemanticElements: a polite live region announcing a loading state; <output> is a form-result element with the wrong semantics. The role is also what makes aria-label valid here — a bare div supports neither.
    <div role="status" aria-busy="true" aria-label={label} className="flex max-w-[680px] flex-col gap-1.5">
      <div className={cn(SLAB_BASE, 'bg-card-sunken flex flex-wrap items-start justify-between gap-6')}>
        <div>
          <div className={cn(bar, 'h-2.5 w-[72px]')} />
          <div className={cn(bar, 'mt-3 h-6 w-[200px]')} />
          <div className={cn(bar, 'mt-3 h-[13px] w-[280px]')} />
        </div>
        <div>
          <div className={cn(bar, 'h-6 w-[86px]')} />
          <div className={cn(bar, 'mt-2 h-[11px] w-16')} />
        </div>
      </div>
      <div className={cn(SLAB_BASE, 'bg-card-sunken')}>
        <div className={cn(bar, 'h-2.5 w-[120px]')} />
        <div className={cn(bar, 'mt-3.5 h-[22px] w-[240px]')} />
        <div className={cn(bar, 'mt-3 h-[13px] w-[330px]')} />
        <div className={cn(bar, 'mt-5 h-[34px] w-[140px]')} />
      </div>
      <div className={cn(SLAB_BASE, 'bg-card-sunken min-h-14')}>
        <div className={cn(bar, 'h-4 w-32')} />
      </div>
    </div>
  )
}

/**
 * The conversion moment — a one-off hero CTA, deliberately **outside the DS button set**, in the
 * same spirit as `BillingBanner`'s documented 36px exception.
 *
 * Its gradient is the same family as the `active` plan slab, which is the whole idea: the button
 * previews the state it buys. Press it and this becomes your colour. Nothing in the DS does that,
 * and reskinning a `PillButton` to fake it would have been the worse outcome.
 *
 * The gradient may keep its 600 endpoint where the `active` slab could not — this ink is pure white
 * at 16px/600, which passes comfortably where the slab's `brand-green-100` secondary ink did not.
 */
function HeroCta({ label, onClick, pending }: { label: string; onClick: () => void; pending: boolean }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-busy={pending || undefined}
      disabled={pending}
      className={cn(
        'group relative inline-flex h-[52px] shrink-0 items-center gap-[11px] overflow-hidden rounded-full pl-[22px] pr-[15px]',
        'bg-[linear-gradient(115deg,oklch(var(--brand-green-800))_0%,oklch(var(--brand-green-600))_100%)]',
        'text-base font-semibold tracking-[-0.005em] text-white',
        'shadow-[0_2px_5px_oklch(var(--brand-green-900)/.25),0_12px_28px_-10px_oklch(var(--brand-green-700)/.55)]',
        'transition-[transform,box-shadow] duration-200 motion-reduce:transition-none',
        'hover:-translate-y-px hover:shadow-[0_3px_7px_oklch(var(--brand-green-900)/.3),0_16px_34px_-10px_oklch(var(--brand-green-700)/.65)]',
        'active:translate-y-px motion-reduce:hover:translate-y-0 motion-reduce:active:translate-y-0',
        'focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-pelorous-500 dark:focus-visible:ring-pelorous-300',
        // The sheen: a white band swept across on hover. Pure decoration, so it goes on ::after and
        // is disabled wholesale under reduced motion.
        'after:pointer-events-none after:absolute after:inset-0 after:-translate-x-[110%]',
        'after:bg-[linear-gradient(105deg,transparent_38%,rgba(255,255,255,.22)_50%,transparent_62%)]',
        'after:transition-transform after:duration-700 after:ease-[cubic-bezier(.3,.7,.2,1)]',
        'hover:after:translate-x-[110%] motion-reduce:after:hidden',
        'disabled:pointer-events-none disabled:opacity-60'
      )}>
      {pending ? (
        <span
          aria-hidden="true"
          className="relative size-4 shrink-0 animate-spin rounded-full border-2 border-white/35 border-t-white [animation-duration:0.7s] motion-reduce:animate-pulse"
        />
      ) : (
        <ParsleyMark filled aria-hidden className="relative h-4 w-[18px] shrink-0 text-brand-green-100" />
      )}
      <span className="relative">{label}</span>
      <span
        aria-hidden
        className={cn(
          'relative flex h-[30px] w-[30px] shrink-0 items-center justify-center rounded-full bg-white/[0.14]',
          'transition-[transform,background-color] duration-200 motion-reduce:transition-none',
          'group-hover:translate-x-0.5 group-hover:bg-white/[0.22] motion-reduce:group-hover:translate-x-0'
        )}>
        <svg
          aria-hidden="true"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.2"
          strokeLinecap="round"
          strokeLinejoin="round"
          className="h-[15px] w-[15px]">
          <path d="M5 12h14" />
          <path d="m13 6 6 6-6 6" />
        </svg>
      </span>
    </button>
  )
}

export const SubscriptionStatusCard = React.forwardRef<HTMLDivElement, SubscriptionStatusCardProps>(
  ({ state, plan, labels = {}, meter, primaryAction, secondaryAction, className }, ref) => {
    if (state === 'loading') {
      return <Skeleton label={labels.loadingLabel ?? 'Loading subscription'} />
    }

    const cfg = SUBSCRIPTION_CARD_STATES[state]
    const hero = cfg.hero
    const chipTone = labels.chipTone ?? 'neutral'

    const action = primaryAction && (
      <PrimaryAction
        kind={primaryAction.kind ?? 'pill-secondary'}
        pending={primaryAction.pending ?? false}
        label={primaryAction.label}
        onClick={primaryAction.onClick}
        deep={cfg.tone === 'deep'}
      />
    )

    return (
      <div ref={ref} className={cn('flex max-w-[680px] flex-col gap-1.5', className)}>
        {cfg.plan && plan && (
          <div
            className={cn(
              SLAB_BASE,
              'relative flex flex-wrap items-start justify-between gap-6 overflow-hidden',
              hero === 'deep' &&
                'bg-[linear-gradient(115deg,oklch(var(--brand-green-800))_0%,oklch(var(--brand-green-700))_100%)] text-white',
              hero === 'lite' &&
                cn(
                  'bg-[color-mix(in_oklab,oklch(var(--brand-green-500))_24%,oklch(var(--card)))] text-brand-green-900',
                  'dark:bg-[color-mix(in_oklab,oklch(var(--brand-green-700))_48%,oklch(var(--card)))] dark:text-brand-green-100'
                ),
              !hero && 'bg-card-sunken'
            )}>
            {hero === 'deep' && <HeroLeaf className="text-white opacity-10" />}
            {hero === 'lite' && <HeroLeaf className="text-brand-green-900 opacity-[0.07] dark:text-brand-green-100 dark:opacity-[0.09]" />}

            <div className="relative">
              <div
                className={cn(
                  'mb-[7px] text-[11px] font-bold uppercase tracking-[0.09em]',
                  hero === 'deep'
                    ? 'text-brand-green-100'
                    : hero === 'lite'
                      ? 'text-brand-green-800 dark:text-brand-green-200'
                      : 'text-muted-foreground'
                )}>
                {labels.eyebrow ?? 'Your plan'}
              </div>
              <div className="flex flex-wrap items-center gap-[11px]">
                <span className="font-brand text-[25px] font-semibold leading-[1.1] tracking-[-0.015em]">{plan.name}</span>
                {labels.chip && (
                  <Chip variant="meta" tone={chipTone} className={chipTone === 'onImage' ? undefined : CHIP_INK[chipTone]}>
                    {labels.chip}
                  </Chip>
                )}
              </div>
              <p
                className={cn(
                  'mt-[7px] max-w-[42ch] text-[13.5px] leading-[1.5]',
                  hero === 'deep'
                    ? 'text-brand-green-100'
                    : hero === 'lite'
                      ? 'text-brand-green-800 dark:text-brand-green-200'
                      : 'text-muted-foreground'
                )}>
                {plan.desc}
              </p>
            </div>

            <div className="relative shrink-0 text-right max-md:text-left">
              <div className="flex items-baseline justify-end gap-[9px] max-md:justify-start">
                <b
                  className={cn(
                    'font-mono text-[27px] font-medium leading-none tracking-[-0.02em] tabular-nums',
                    labels.priceNote && 'line-through decoration-2 opacity-55'
                  )}>
                  {plan.price}
                </b>
                {labels.priceNote && (
                  <span
                    className={cn(
                      'whitespace-nowrap text-[13px] font-medium',
                      hero === 'lite' && 'text-brand-green-800 dark:text-brand-green-200'
                    )}>
                    {labels.priceNote}
                  </span>
                )}
              </div>
              {!labels.priceNote && (
                <span
                  className={cn(
                    'mt-[5px] block text-[12.5px]',
                    hero === 'deep'
                      ? 'text-brand-green-100'
                      : hero === 'lite'
                        ? 'text-brand-green-800 dark:text-brand-green-200'
                        : 'text-muted-foreground'
                  )}>
                  {plan.per}
                </span>
              )}
            </div>
          </div>
        )}

        {/* biome-ignore lint/a11y/useSemanticElements: a polite live region for a standing billing
            condition; <output> is a form-result element with the wrong semantics. `status`, not
            `alert` — the user chose to open this page, so nothing here should steal focus. */}
        <div className={cn(SLAB_BASE, SLAB[cfg.tone])} role="status">
          {state === 'provisioning' ? (
            <div className="flex items-start gap-4">
              <span className="mt-1 shrink-0 text-brand-green-600 dark:text-brand-green-300">
                <Loader variant="dots" size={26} aria-label={labels.head ?? 'Setting up'} />
              </span>
              <div>
                <div className="font-brand text-2xl font-semibold leading-[1.18] tracking-[-0.015em]">{labels.head}</div>
                <p className="mt-[11px] max-w-[54ch] text-[14.5px] leading-[1.58] opacity-90">{labels.body}</p>
              </div>
            </div>
          ) : (
            <>
              {labels.kicker && <div className="mb-3 text-[11px] font-bold uppercase tracking-[0.09em] opacity-80">{labels.kicker}</div>}

              {labels.figure ? (
                <div className="flex items-baseline gap-2.5 font-mono text-[40px] font-medium leading-none tracking-[-0.03em] tabular-nums max-md:text-[33px]">
                  {labels.figure}
                  <em className="font-sans text-[15.5px] font-medium not-italic tracking-normal opacity-90">{labels.figureUnit}</em>
                </div>
              ) : (
                <div
                  className={cn(
                    'font-brand text-2xl font-semibold leading-[1.18] tracking-[-0.015em] text-balance max-md:text-[21px]',
                    HEAD_INK[cfg.tone]
                  )}>
                  {labels.head}
                </div>
              )}

              {meter != null && (
                <div className="mt-4 h-2 max-w-[320px] overflow-hidden rounded-full bg-[color-mix(in_oklab,currentColor_16%,transparent)]">
                  <div className="h-full rounded-full bg-current" style={{ width: `${(meter * 100).toFixed(1)}%` }} />
                </div>
              )}

              <p className="mt-[11px] max-w-[54ch] text-[14.5px] leading-[1.58] opacity-90 [&_b]:font-semibold">{labels.body}</p>

              {labels.card && (
                <div className="mt-3 flex items-center gap-2 text-[13px] opacity-85">
                  <CreditCard className="h-[15px] w-[15px] shrink-0" strokeWidth={1.8} />
                  {labels.card}
                </div>
              )}

              {action && (
                <div className="mt-5 flex flex-wrap items-center gap-3.5">
                  {action}
                  {labels.hint && <span className="text-[12.5px] leading-[1.4] opacity-85">{labels.hint}</span>}
                </div>
              )}
            </>
          )}
        </div>

        {cfg.foot && (
          <div className={cn('flex flex-wrap items-center gap-4 rounded-[10px] bg-card-sunken py-[13px] pl-4 pr-5', OUTER)}>
            <PillButton variant="ghost" onClick={secondaryAction?.onClick}>
              {labels.manage ?? 'Manage billing'}
              <ExternalLink className="h-3.5 w-3.5" />
            </PillButton>
            {/* `muted-foreground`, deliberately not `text-faint` — faint measures 2.88:1 on
                card-sunken at this size, an AA failure. Folded into #1214. */}
            <span className="ml-auto text-[12.5px] leading-[1.45] text-muted-foreground max-md:ml-0">{labels.fine}</span>
          </div>
        )}
      </div>
    )
  }
)
SubscriptionStatusCard.displayName = 'SubscriptionStatusCard'

/** The action ramp, matching `BillingBanner`'s escalation plus the trial-conversion hero. */
function PrimaryAction({
  kind,
  label,
  onClick,
  deep,
  pending
}: { kind: ActionKind; label: string; onClick: () => void; deep: boolean; pending: boolean }) {
  if (kind === 'hero') return <HeroCta label={label} onClick={onClick} pending={pending} />
  // 36px DS default everywhere else — settled by #1252, decision 3. Never `size="lg"`.
  if (kind === 'on-deep') {
    return (
      <PillButton variant="on-green-primary" onClick={onClick} pending={pending}>
        {label}
      </PillButton>
    )
  }
  if (kind === 'pill-primary') {
    return (
      <PillButton variant="primary" onClick={onClick} pending={pending}>
        {label}
      </PillButton>
    )
  }
  // Solid amber, via `--primary` (OKLCH hue 72.8). `Button`'s default size is h-9 — the same 36px as
  // the pills beside it — so the jeopardy states get the design's solid escalation with no reskin.
  if (kind === 'solid') {
    return (
      <Button variant="default" onClick={onClick} pending={pending}>
        {label}
      </Button>
    )
  }
  return (
    <PillButton variant={deep ? 'on-green-secondary' : 'secondary'} onClick={onClick} pending={pending}>
      {label}
    </PillButton>
  )
}
